import { supabase } from '../lib/supabase';

export const workerScheduleService = {
  /**
   * Get all workers (role=worker) for a company
   */
  async getCompanyWorkers(companyId) {
    try {
      // Include pending-invite workers (is_active=false) so admins can
      // schedule them before they accept. The UI surfaces a "Pending invite"
      // badge on rows where is_active=false.
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, is_active, company_id')
        ?.eq('company_id', companyId)
        ?.in('role', ['worker', 'site_manager'])
        ?.order('full_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Get assignments for a company within a date range
   */
  async getAssignmentsRange(companyId, startDate, endDate) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_worker_assignments_range', {
          p_company_id: companyId,
          p_start_date: startDate,
          p_end_date: endDate,
        });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Set (upsert) a worker's assignment for a specific date
   */
  async setAssignment(userId, siteId, companyId, assignmentDate) {
    try {
      const { data: currentUser } = await supabase?.auth?.getUser();
      const assignedBy = currentUser?.user?.id;

      const { data, error } = await supabase
        ?.from('worker_site_assignments')
        ?.upsert(
          {
            user_id: userId,
            site_id: siteId,
            company_id: companyId,
            assignment_date: assignmentDate,
            assigned_by: assignedBy,
            updated_at: new Date()?.toISOString(),
          },
          { onConflict: 'user_id,assignment_date' }
        )
        ?.select('id, user_id, site_id, assignment_date')
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  /**
   * Remove a worker's assignment for a specific date
   */
  async removeAssignment(userId, assignmentDate) {
    try {
      const { error } = await supabase
        ?.from('worker_site_assignments')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('assignment_date', assignmentDate);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message } };
    }
  },

  /**
   * Get upcoming assignments for a worker (today + next N days)
   */
  async getWorkerUpcomingAssignments(userId, days = 7) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_worker_upcoming_assignments', {
          p_user_id: userId,
          p_days: days,
        });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },
};
